'use client';

import { useState, useCallback, useEffect } from 'react';
import { aiModelsApi, LTX2Response, LTX2OptionsResponse } from '@/lib/ai-models-api';
import { api, default as apiClient } from '@/lib/api';
import { HealthResponse } from '@/lib/types';
import StatusBar from '@/components/StatusBar';

type Tab = 't2v' | 'i2v';

export default function LTXPage() {
  const [tab, setTab] = useState<Tab>('t2v');
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [stats, setStats] = useState<{ processing: number; pending: number; completed: number } | null>(null);

  // Form state
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [width, setWidth] = useState(768);
  const [height, setHeight] = useState(512);
  const [numFrames, setNumFrames] = useState(97);
  const [steps, setSteps] = useState(40);
  const [guidanceScale, setGuidanceScale] = useState(3.0);
  const [stgScale, setStgScale] = useState(1.0);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [seed, setSeed] = useState<number | null>(null);

  // I2V state
  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageStrength, setImageStrength] = useState(1.0);

  // UI state
  const [options, setOptions] = useState<LTX2OptionsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LTX2Response | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const h = await api.getHealth();
      setHealth(h);
      setStats({ processing: 0, pending: 0, completed: 0 });
    } catch {}
  }, []);

  useEffect(() => {
    fetchData();
    aiModelsApi.ltx2Options().then(setOptions).catch(console.error);
  }, [fetchData]);

  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]);
      reader.readAsDataURL(file);
    });
    setSourceImage(base64);
    setImagePreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
  }, []);

  const handleResolutionChange = (resString: string) => {
    const [w, h] = resString.split('x').map(Number);
    setWidth(w);
    setHeight(h);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    if (tab === 'i2v' && !sourceImage) return;

    setLoading(true);
    setError(null);

    try {
      let response: LTX2Response;

      if (tab === 't2v') {
        response = await aiModelsApi.ltx2T2V({
          prompt,
          negative_prompt: negativePrompt || undefined,
          width,
          height,
          num_frames: numFrames,
          num_inference_steps: steps,
          guidance_scale: guidanceScale,
          stg_scale: stgScale,
          audio_enabled: audioEnabled,
          seed,
        });
      } else {
        response = await aiModelsApi.ltx2I2V(sourceImage!, {
          prompt,
          negative_prompt: negativePrompt || undefined,
          width,
          height,
          num_frames: numFrames,
          num_inference_steps: steps,
          guidance_scale: guidanceScale,
          stg_scale: stgScale,
          image_strength: imageStrength,
          audio_enabled: audioEnabled,
          seed,
        });
      }

      setResult(response);

      // Auto-save to video gallery
      try {
        // Use thumbnail from AI service (first frame), or source image for I2V
        const thumbnailImage = response.thumbnail || (tab === 'i2v' ? sourceImage : undefined);
        await apiClient.saveVideoToGallery({
          video: response.video,
          prompt: response.prompt,
          source_image: thumbnailImage || undefined,
          width: response.width,
          height: response.height,
          frames: response.num_frames,
          fps: response.fps,
          has_audio: response.has_audio,
          seed: response.seed,
          inference_time_ms: response.inference_time_ms,
          model: 'ltx-2',
        });
      } catch (saveErr) {
        console.error('Failed to save to gallery:', saveErr);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <StatusBar health={health} stats={stats} onRefresh={fetchData} />

      {/* Sub-header with tabs */}
      <div className="border-b border-[var(--border-subtle)] bg-[var(--bg-elevated)]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between py-3">
            <div>
              <h1 className="text-lg font-semibold">LTX-2.3 Audio-Video Studio</h1>
              <p className="text-xs text-[var(--text-muted)]">22B joint audio-video generation by Lightricks</p>
            </div>
            <div className="flex gap-1">
              {[
                { id: 't2v' as Tab, label: 'Text to Video' },
                { id: 'i2v' as Tab, label: 'Image to Video' },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => { setTab(t.id); setResult(null); setError(null); }}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    tab === t.id
                      ? 'bg-[var(--accent)] text-black'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Left: Parameters */}
            <div>
              <form onSubmit={handleSubmit} className="bg-[var(--bg-elevated)] rounded-xl p-6 space-y-5">

                {/* I2V: Image Upload */}
                {tab === 'i2v' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Source Image</label>
                    {imagePreview ? (
                      <div className="relative">
                        <img src={imagePreview} alt="Source" className="w-full h-48 object-contain bg-[var(--bg-primary)] rounded-lg" />
                        <button
                          type="button"
                          onClick={() => { setSourceImage(null); setImagePreview(null); setResult(null); }}
                          className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-[var(--border-subtle)] rounded-lg cursor-pointer hover:border-[var(--accent)] transition-colors">
                        <svg className="w-10 h-10 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                        <span className="mt-2 text-sm text-[var(--text-muted)]">Upload image to animate</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>
                )}

                {/* Prompt */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {tab === 't2v' ? 'Prompt' : 'Motion Description'}
                    <span className="text-[var(--text-muted)] font-normal ml-2">({prompt.length} chars)</span>
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={tab === 't2v'
                      ? 'Describe the scene in detail: setting, lighting, atmosphere, motion, camera movement, sounds...'
                      : 'Describe how the image should animate: motion, action, camera movement...'}
                    rows={4}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none resize-none"
                  />
                </div>

                {/* Negative Prompt */}
                <div>
                  <label className="block text-sm font-medium mb-2">Negative Prompt</label>
                  <input
                    type="text"
                    value={negativePrompt}
                    onChange={(e) => setNegativePrompt(e.target.value)}
                    placeholder={options?.default_negative_prompt || 'What to avoid...'}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  />
                </div>

                {/* Resolution + Frames row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Resolution</label>
                    <select
                      value={`${width}x${height}`}
                      onChange={(e) => handleResolutionChange(e.target.value)}
                      className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                    >
                      {options?.resolutions.map((res) => (
                        <option key={`${res.width}x${res.height}`} value={`${res.width}x${res.height}`}>
                          {res.label}
                        </option>
                      )) || (
                        <>
                          <option value="768x512">3:2 Landscape</option>
                          <option value="512x512">1:1 Square</option>
                        </>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Duration</label>
                    <select
                      value={numFrames}
                      onChange={(e) => setNumFrames(parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                    >
                      {options?.frame_counts.map((fc) => (
                        <option key={fc.frames} value={fc.frames}>
                          {fc.frames}f &middot; {fc.duration}
                        </option>
                      )) || (
                        <>
                          <option value="25">25f &middot; ~1 second</option>
                          <option value="97">97f &middot; ~4 seconds</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>

                {/* Audio toggle */}
                <div className="flex items-center gap-3 p-3 bg-[var(--bg-primary)] rounded-lg border border-[var(--border-subtle)]">
                  <input
                    type="checkbox"
                    id="audio"
                    checked={audioEnabled}
                    onChange={(e) => setAudioEnabled(e.target.checked)}
                    className="w-4 h-4 accent-[var(--accent)]"
                  />
                  <label htmlFor="audio" className="text-sm font-medium flex-1">
                    Generate Audio
                  </label>
                  <span className="text-xs text-[var(--text-muted)]">Synchronized audio with video</span>
                </div>

                {/* I2V: Image Strength */}
                {tab === 'i2v' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Image Strength: {imageStrength.toFixed(2)}
                    </label>
                    <input
                      type="range" min="0" max="1" step="0.05"
                      value={imageStrength}
                      onChange={(e) => setImageStrength(parseFloat(e.target.value))}
                      className="w-full accent-[var(--accent)]"
                    />
                    <p className="text-xs text-[var(--text-muted)] mt-1">How closely to match the source image</p>
                  </div>
                )}

                {/* Advanced Settings */}
                <details className="bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg">
                  <summary className="cursor-pointer font-medium p-3 text-sm">Advanced Settings</summary>
                  <div className="p-3 pt-0 space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Inference Steps: {steps}</label>
                      <input type="range" min="10" max="50" value={steps}
                        onChange={(e) => setSteps(parseInt(e.target.value))}
                        className="w-full accent-[var(--accent)]" />
                      <p className="text-xs text-[var(--text-muted)] mt-1">40 recommended for best quality</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Guidance Scale: {guidanceScale.toFixed(1)}</label>
                      <input type="range" min="1.0" max="10.0" step="0.1" value={guidanceScale}
                        onChange={(e) => setGuidanceScale(parseFloat(e.target.value))}
                        className="w-full accent-[var(--accent)]" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Temporal Coherence (STG): {stgScale.toFixed(1)}</label>
                      <input type="range" min="0.0" max="3.0" step="0.1" value={stgScale}
                        onChange={(e) => setStgScale(parseFloat(e.target.value))}
                        className="w-full accent-[var(--accent)]" />
                      <p className="text-xs text-[var(--text-muted)] mt-1">Higher = more coherent motion, 1.0 recommended</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Seed</label>
                      <input type="number" value={seed ?? ''} placeholder="Random"
                        onChange={(e) => setSeed(e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-3 py-2 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none" />
                    </div>
                  </div>
                </details>

                {/* Info */}
                <div className="text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                  <strong>Note:</strong> First generation loads the 22B model (~10-15 min). Subsequent generations are faster (~1-3 min).
                  Two-stage pipeline: generate at lower res, then upsample with refinement.
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!prompt.trim() || (tab === 'i2v' && !sourceImage) || loading}
                  className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Generating...' : `Generate ${tab === 't2v' ? 'Text-to-Video' : 'Image-to-Video'}`}
                </button>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
              </form>
            </div>

            {/* Right: Results */}
            <div>
              <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Generated Video</h2>

                {/* Empty state */}
                {!result && !loading && (
                  <div className="flex flex-col items-center justify-center py-32 text-center">
                    <svg className="w-16 h-16 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <p className="text-[var(--text-muted)]">
                      {tab === 't2v' ? 'Enter a prompt to generate video with audio' : 'Upload an image and describe the motion'}
                    </p>
                  </div>
                )}

                {/* Loading */}
                {loading && (
                  <div className="flex flex-col items-center justify-center py-32">
                    <div className="w-12 h-12 border-3 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-[var(--text-muted)]">Generating video...</p>
                    <p className="text-xs text-[var(--text-muted)] mt-1">This may take several minutes</p>
                  </div>
                )}

                {/* Result */}
                {result && (
                  <div className="space-y-4">
                    {/* Video Player */}
                    <div className="border border-[var(--border-subtle)] rounded-lg overflow-hidden">
                      <video
                        src={`data:video/mp4;base64,${result.video}`}
                        controls
                        autoPlay
                        loop
                        className="w-full"
                      />
                    </div>

                    {/* Download */}
                    <a
                      href={`data:video/mp4;base64,${result.video}`}
                      download={`ltx2_${result.mode}_${result.seed || 'random'}.mp4`}
                      className="block w-full py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors text-center"
                    >
                      Download Video {result.has_audio ? '(with Audio)' : ''}
                    </a>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-[var(--bg-primary)] rounded-lg">
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Resolution</p>
                        <p className="text-lg font-semibold">{result.width}x{result.height}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Duration</p>
                        <p className="text-lg font-semibold">{result.duration_seconds.toFixed(1)}s</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Frames</p>
                        <p className="text-lg font-semibold">{result.num_frames} @ {result.fps}fps</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Audio</p>
                        <p className="text-lg font-semibold">{result.has_audio ? 'Yes' : 'No'}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3 p-4 bg-[var(--bg-primary)] rounded-lg">
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Mode</p>
                        <p className="text-sm font-medium">{result.mode.toUpperCase()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Seed</p>
                        <p className="text-sm font-medium">{result.seed ?? 'Random'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] uppercase">Time</p>
                        <p className="text-sm font-medium">{(result.inference_time_ms / 1000).toFixed(1)}s</p>
                      </div>
                    </div>

                    {/* Prompt */}
                    <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                      <p className="text-xs text-[var(--text-muted)] uppercase mb-2">Prompt</p>
                      <p className="text-sm">{result.prompt}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
